import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import faTranslation from './locales/fa.json';
import enTranslation from './locales/en.json';
import jaTranslation from './locales/ja.json';
import deTranslation from './locales/de.json';
import ptTranslation from './locales/pt.json';
import frTranslation from './locales/fr.json';
import itTranslation from './locales/it.json';
import esTranslation from './locales/es.json';
import koTranslation from './locales/ko.json';
import hiTranslation from './locales/hi.json';
import zhTranslation from './locales/zh.json';
import adminTranslations from './admin';

// افزودن زبان جدید = یک import بالا + یک ردیف اینجا + (در صورت داشتن دامنه‌ی
// اختصاصی) یک ردیف در DOMAIN_LANGUAGE_MAP. بقیه‌ی منطق (ساب‌دامنه، fallback،
// جهت RTL، لیست زبان‌های پشتیبانی‌شده) خودکار کار می‌کند.
const resources = {
  fa: { translation: faTranslation },
  en: { translation: enTranslation },
  ja: { translation: jaTranslation },
  de: { translation: deTranslation },
  pt: { translation: ptTranslation },
  fr: { translation: frTranslation },
  it: { translation: itTranslation },
  es: { translation: esTranslation },
  ko: { translation: koTranslation },
  hi: { translation: hiTranslation },
  zh: { translation: zhTranslation },
};

// ترجمه‌های پنل مدیریت (src/i18n/admin/*) زیر کلید `admin` به هر زبان اضافه می‌شوند
// تا کامپوننت‌های ادمین با t('admin.<Component>.<key>') یا t('admin.common.<key>') کار کنند.
Object.keys(resources).forEach((lang) => {
  resources[lang].translation.admin = adminTranslations[lang] || {};
});

export const SUPPORTED_LANGUAGES = Object.keys(resources);
export const DEFAULT_LANGUAGE = 'fa';

// زبان‌هایی که راست‌به‌چپ نوشته می‌شوند (برای تنظیم dir صفحه).
export const RTL_LANGUAGES = ['fa', 'ar', 'he'];
export const isRTL = (lang) => RTL_LANGUAGES.includes(lang);

// نگاشت دامنه‌ی اختصاصی هر زبان → کد زبان (قابل‌تنظیم).
// هر زبان می‌تواند دامنه‌ی مستقل خودش را داشته باشد؛ زبان‌های بدون دامنه‌ی
// اختصاصی روی ساب‌دامنه‌ی <lang>.potterheads.org بالا می‌آیند
// (مثل ja.potterheads.org, de.potterheads.org, fr.potterheads.org ...).
const DOMAIN_LANGUAGE_MAP = {
  'potterhead.ir': 'fa',
  'potterheads.org': 'en',
};

/**
 * تشخیص زبان از hostname با اولویت:
 *   1) ساب‌دامنه‌ی زبان: fr.potterheads.org → fr (اگر پشتیبانی شود)
 *   2) دامنه‌ی اختصاصی در DOMAIN_LANGUAGE_MAP (تطابق پسوندی، شامل www)
 *   3) پیش‌فرض
 */
export const detectLanguageFromHost = (hostname) => {
  const host = String(hostname || '').toLowerCase();

  // 1) ساب‌دامنه‌ی زبان: <lang>.example.com
  const labels = host.split('.');
  if (labels.length > 2 && SUPPORTED_LANGUAGES.includes(labels[0])) {
    return labels[0];
  }

  // 2) دامنه‌ی اختصاصی (تطابق دقیق یا www./ساب‌دامنه‌ی همان دامنه)
  for (const [domain, lang] of Object.entries(DOMAIN_LANGUAGE_MAP)) {
    if (host === domain || host.endsWith(`.${domain}`)) {
      return lang;
    }
  }

  return DEFAULT_LANGUAGE;
};

const getInitialLanguage = () => {
  // در محیط واقعی، hostname منبع اصلی زبان است؛ سوییچر هم کاربر را به همان
  // دامنه/ساب‌دامنه هدایت می‌کند. این باعث می‌شود ورود مستقیم به
  // ja.potterheads.org با مقدار قدیمی localStorage به زبان دیگری نیفتد.
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return detectLanguageFromHost(host);
    }
  }

  // انتخاب صریح کاربر در توسعه‌ی محلی بر تشخیص دامنه اولویت دارد.
  const savedLang = localStorage.getItem('language');
  if (savedLang && SUPPORTED_LANGUAGES.includes(savedLang)) {
    return savedLang;
  }
  if (typeof window !== 'undefined' && window.location) {
    return detectLanguageFromHost(window.location.hostname);
  }
  return DEFAULT_LANGUAGE;
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    // کلیدهای ترجمه‌نشده‌ی زبان‌های جدید به انگلیسی fallback می‌شوند (نه فارسی)،
    // چون این زبان‌ها روی سایت بین‌المللی بالا می‌آیند.
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false,
    },
  });

// جهت صفحه را با زبان جاری هماهنگ نگه می‌دارد (RTL برای فارسی، LTR برای بقیه).
const applyDir = (lng) => {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lng;
    document.documentElement.dir = isRTL(lng) ? 'rtl' : 'ltr';
  }
};
applyDir(i18n.language);
i18n.on('languageChanged', applyDir);

const COUNTRY_LANGUAGE_MAP = {
  IR: 'fa',
  JP: 'ja',
  DE: 'de',
  PT: 'pt', BR: 'pt',
  FR: 'fr',
  IT: 'it',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es',
  KR: 'ko',
  IN: 'hi',
  CN: 'zh', HK: 'zh', MO: 'zh', TW: 'zh', SG: 'zh'
};

export const detectLanguageFromIP = async () => {
  // اگر ترجیح کاربر قبلاً ذخیره شده باشد، کاری انجام نمی‌دهیم (انتخاب کاربر اولویت دارد)
  if (localStorage.getItem('language')) {
    return;
  }

  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return;
    const data = await res.json();
    const country = String(data?.country_code || '').toUpperCase();
    const detectedLang = COUNTRY_LANGUAGE_MAP[country];
    
    if (detectedLang && SUPPORTED_LANGUAGES.includes(detectedLang)) {
      const currentLang = i18n.language;
      if (detectedLang !== currentLang) {
        localStorage.setItem('language', detectedLang);
        i18n.changeLanguage(detectedLang);
        
        // هدایت کاربر به دامنه/ساب‌دامنه مناسب در محیط واقعی
        const host = window.location.hostname;
        if (host !== 'localhost' && host !== '127.0.0.1') {
          let targetUrl = '';
          if (detectedLang === 'fa') {
            targetUrl = 'https://potterhead.ir';
          } else if (detectedLang === 'en') {
            targetUrl = 'https://potterheads.org';
          } else {
            targetUrl = `https://${detectedLang}.potterheads.org`;
          }
          window.location.href = targetUrl + window.location.pathname + window.location.search;
        }
      }
    }
  } catch (error) {
    console.error('Failed to detect language from IP:', error);
  }
};

// اجرای خودکار تشخیص IP پس از لود صفحه
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(detectLanguageFromIP, 1500);
  });
}

export default i18n;
